/**
 * 加密存储工具
 *
 * 分层加密策略：
 *   - 安全上下文（HTTPS / localhost）：Web Crypto API AES-GCM 256 位加密
 *   - 非安全上下文（HTTP 部署）：XOR + Base64 混淆，防止肉眼直读
 *
 * 注意：前端加密只能提供混淆保护，高安全场景建议改用 HttpOnly Cookie 存储 Token。
 *
 * @module utils/crypto-storage
 */

// ────────────────────────────────────────────────────────────────────────────────
// 常量
// ────────────────────────────────────────────────────────────────────────────────

import { logger } from '@/utils/logger'

const PREFIX_AES = 'aes:'
const PREFIX_XOR = 'xor:'

/** AES-GCM 参数 */
const AES_CONFIG = {
  ALGORITHM: 'AES-GCM' as const,
  KEY_LENGTH: 256,
  IV_LENGTH: 12,
  KEY_ITERATIONS: 100_000,
}

/** XOR 密钥材料（混淆用，不用于真正保密） */
const XOR_SECRET = 'datamanus-xor-obfuscation-key-2024'

// ────────────────────────────────────────────────────────────────────────────────
// 环境检测
// ────────────────────────────────────────────────────────────────────────────────

/**
 * 检测 Web Crypto API 是否可用（需要 HTTPS 或 localhost）
 */
function isSecureContext(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.subtle.importKey === 'function'
  )
}

// ────────────────────────────────────────────────────────────────────────────────
// 策略一：AES-GCM（安全上下文）
// ────────────────────────────────────────────────────────────────────────────────

const SALT = new TextEncoder().encode('datamanus-secure-storage-salt-v1')

async function deriveAesKey(): Promise<CryptoKey> {
  const material = ['datamanus-web-app', navigator.userAgent, window.location.origin].join('|')
  const rawKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(material),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: SALT, iterations: AES_CONFIG.KEY_ITERATIONS, hash: 'SHA-256' },
    rawKey,
    { name: AES_CONFIG.ALGORITHM, length: AES_CONFIG.KEY_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function aesEncrypt(plaintext: string): Promise<string> {
  const key = await deriveAesKey()
  const iv = crypto.getRandomValues(new Uint8Array(AES_CONFIG.IV_LENGTH))
  const encrypted = await crypto.subtle.encrypt(
    { name: AES_CONFIG.ALGORITHM, iv },
    key,
    new TextEncoder().encode(plaintext),
  )
  const combined = new Uint8Array(iv.length + encrypted.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(encrypted), iv.length)
  return PREFIX_AES + uint8ToBase64(combined)
}

async function aesDecrypt(ciphertext: string): Promise<string> {
  const combined = base64ToUint8(ciphertext.slice(PREFIX_AES.length))
  const iv = combined.slice(0, AES_CONFIG.IV_LENGTH)
  const data = combined.slice(AES_CONFIG.IV_LENGTH)
  const key = await deriveAesKey()
  const decrypted = await crypto.subtle.decrypt({ name: AES_CONFIG.ALGORITHM, iv }, key, data)
  return new TextDecoder().decode(decrypted)
}

// ────────────────────────────────────────────────────────────────────────────────
// 策略二：XOR + Base64（非安全上下文降级）
// ────────────────────────────────────────────────────────────────────────────────

function xorObfuscate(text: string, key: string): string {
  return text
    .split('')
    .map((ch, i) => String.fromCharCode(ch.charCodeAt(0) ^ key.charCodeAt(i % key.length)))
    .join('')
}

function xorEncrypt(plaintext: string): string {
  const obfuscated = xorObfuscate(plaintext, XOR_SECRET)
  return PREFIX_XOR + btoa(unescape(encodeURIComponent(obfuscated)))
}

function xorDecrypt(ciphertext: string): string {
  const obfuscated = decodeURIComponent(escape(atob(ciphertext.slice(PREFIX_XOR.length))))
  return xorObfuscate(obfuscated, XOR_SECRET)
}

// ────────────────────────────────────────────────────────────────────────────────
// 辅助工具
// ────────────────────────────────────────────────────────────────────────────────

function uint8ToBase64(buf: Uint8Array): string {
  // 分片处理，避免 buf 超过 ~30KB 时 String.fromCharCode(...buf) 栈溢出
  const CHUNK_SIZE = 8192
  let result = ''
  for (let i = 0; i < buf.length; i += CHUNK_SIZE) {
    const chunk = buf.subarray(i, i + CHUNK_SIZE)
    result += String.fromCharCode(...chunk)
  }
  return btoa(result)
}

function base64ToUint8(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
}

/** 判断存储值是否为本工具加密过的格式 */
function isEncryptedValue(value: string): boolean {
  return value.startsWith(PREFIX_AES) || value.startsWith(PREFIX_XOR)
}

// ────────────────────────────────────────────────────────────────────────────────
// 统一加解密入口
// ────────────────────────────────────────────────────────────────────────────────

/**
 * 加密字符串
 * - 安全上下文：AES-GCM
 * - 非安全上下文：XOR + Base64
 */
async function encryptValue(plaintext: string): Promise<string> {
  if (isSecureContext()) {
    return aesEncrypt(plaintext)
  }
  return xorEncrypt(plaintext)
}

/**
 * 解密字符串，自动识别加密策略
 * - `aes:` 前缀：AES-GCM 解密
 * - `xor:` 前缀：XOR 解密
 * - 无前缀：兼容旧明文数据，原样返回
 */
async function decryptValue(ciphertext: string): Promise<string> {
  if (ciphertext.startsWith(PREFIX_AES)) {
    if (!isSecureContext()) {
      // 当前为 HTTP 环境，无法解密 HTTPS 下存储的 AES 数据，返回空让调用方重新登录
      logger.warn('[crypto-storage] 非安全上下文，无法解密 AES 数据，请重新登录')
      return ''
    }
    return aesDecrypt(ciphertext)
  }

  if (ciphertext.startsWith(PREFIX_XOR)) {
    return xorDecrypt(ciphertext)
  }

  // 旧版明文数据兼容
  return ciphertext
}

// ────────────────────────────────────────────────────────────────────────────────
// 公开 API
// ────────────────────────────────────────────────────────────────────────────────

/**
 * 安全存储对象，提供类似 localStorage 的 API，自动按上下文选择加密策略
 */
export const secureStorage = {
  async setItem(key: string, value: string): Promise<void> {
    if (!value) {
      localStorage.removeItem(key)
      return
    }
    const encrypted = await encryptValue(value)
    localStorage.setItem(key, encrypted)
  },

  async getItem(key: string): Promise<string | null> {
    const stored = localStorage.getItem(key)
    if (stored === null) return null
    const result = await decryptValue(stored)
    // 解密结果为空且原数据确实存在，说明加密数据无法解密（如切换了协议），清除旧数据
    if (!result && isEncryptedValue(stored)) {
      localStorage.removeItem(key)
      return null
    }
    return result || null
  },

  removeItem(key: string): void {
    localStorage.removeItem(key)
  },

  clear(): void {
    localStorage.clear()
  },

  /**
   * 当前使用的加密策略
   */
  getStrategy(): 'aes-gcm' | 'xor-base64' {
    return isSecureContext() ? 'aes-gcm' : 'xor-base64'
  },

  /**
   * 是否处于安全上下文（HTTPS / localhost）
   */
  isSecure(): boolean {
    return isSecureContext()
  },
}

// ────────────────────────────────────────────────────────────────────────────────
// Token 专用存储
// ────────────────────────────────────────────────────────────────────────────────

export const tokenStorage = {
  ACCESS_TOKEN_KEY: 'token',
  REFRESH_TOKEN_KEY: 'refreshToken',

  async saveTokens(accessToken: string, refreshToken?: string): Promise<void> {
    await secureStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken)
    if (refreshToken) {
      await secureStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken)
    }
  },

  async getAccessToken(): Promise<string | null> {
    return secureStorage.getItem(this.ACCESS_TOKEN_KEY)
  },

  async getRefreshToken(): Promise<string | null> {
    return secureStorage.getItem(this.REFRESH_TOKEN_KEY)
  },

  clearTokens(): void {
    secureStorage.removeItem(this.ACCESS_TOKEN_KEY)
    secureStorage.removeItem(this.REFRESH_TOKEN_KEY)
  },
}

export default secureStorage
